import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Star, Phone, Globe, Clock, CalendarCheck, Loader2, MessageCircle, ParkingSquare, Shield, Car, Tag, Plane, CheckCircle2 } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Fade-in-on-scroll wrapper, IntersectionObserver-based (mirrors the reveal
// pattern from askrent.pl). No-op when the viewer prefers reduced motion.
function Reveal({ children, className = "", style }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={style}
      className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
    >
      {children}
    </div>
  );
}

// Wlasciciel firmy edytuje te wartosci w builderze — bez walidacji ktos mogly
// wpisac np. "javascript:..." jako link social albo dowolny host jako
// widget_url. Ogranicz do http(s) (linki) / zaufanego hosta Slotify (iframe).
function isSafeHttpUrl(url) {
  try {
    return ["http:", "https:"].includes(new URL(url).protocol);
  } catch {
    return false;
  }
}

const SLOTIFY_WIDGET_HOST = "slotify.dev.sorto.ai";
function isTrustedSlotifyWidgetUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === "https:" && u.hostname === SLOTIFY_WIDGET_HOST;
  } catch {
    return false;
  }
}

// Slotify widget posts its content height via postMessage so the embedding
// iframe can grow/shrink with the booking step (date picker -> form -> success)
// instead of showing a fixed, often-too-short or wastefully-tall box.
function useSlotifyWidgetResize(iframeRef) {
  useEffect(() => {
    function onMessage(event) {
      if (event.origin !== `https://${SLOTIFY_WIDGET_HOST}`) return;
      if (event.data?.source !== "slotify-widget" || event.data?.type !== "resize") return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      iframeRef.current.style.minHeight = `${event.data.height}px`;
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframeRef]);
}

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`w-5 h-5 ${s <= Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

// ============ BLOCK RENDERERS ============

function HeroBlock({ data, business, primary }) {
  const photo = data.use_business_photo ? business.photos?.[0] : data.photo_url;
  return (
    <div className="relative h-80 sm:h-96 overflow-hidden">
      {photo && <img src={photo} alt={business.name} className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent" />
      <div className="absolute inset-0 flex items-end">
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-8 text-white">
          <Badge className="bg-white/20 backdrop-blur text-white mb-3">{business.category}</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-2">{data.title || business.name}</h1>
          {data.subtitle && <p className="text-lg text-white/90 mb-2">{data.subtitle}</p>}
          <div className="flex items-center gap-3 text-sm">
            <StarRating rating={business.rating} />
            <span className="font-semibold">{business.rating}</span>
            <span className="text-white/80">({business.reviews_count} opinii)</span>
            <span className="text-white/60">·</span>
            <span className="text-white/80">{business.city}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AboutBlock({ data, dark }) {
  if (!data.body) return null;
  return (
    <Card className={dark ? "bg-[#1a1e26] border-[#2c313d] text-white" : ""}>
      <CardContent className="p-6">
        <h2 className="font-semibold text-lg mb-3">{data.heading}</h2>
        <p className={`leading-relaxed whitespace-pre-line ${dark ? "text-slate-400" : "text-slate-700"}`}>{data.body}</p>
      </CardContent>
    </Card>
  );
}

function ListBlock({ data, accent, primary, dark }) {
  if (!data.items?.length) return null;
  return (
    <Card className={dark ? "bg-[#1a1e26] border-[#2c313d] text-white" : ""}>
      <CardContent className="p-6">
        <h2 className="font-semibold text-lg mb-4">{data.heading}</h2>
        <ul className="space-y-2">
          {data.items.map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: primary || "#10b981" }} />
              <span className={dark ? "text-slate-400" : "text-slate-700"}>{item}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function GalleryBlock({ data, dark }) {
  if (!data.photos?.length) return null;
  return (
    <Card className={dark ? "bg-[#1a1e26] border-[#2c313d] text-white" : ""}>
      <CardContent className="p-6">
        <h2 className="font-semibold text-lg mb-4">{data.heading}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {data.photos.map((url, i) => (
            <div key={i} className={`aspect-square rounded-lg overflow-hidden ${dark ? "bg-[#12151b]" : "bg-slate-100"}`}>
              <img src={url} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SlotifyBlock({ data, business, primary, dark }) {
  const slug = data.slug || business.slotify_slug;
  const iframeRef = useRef(null);
  useSlotifyWidgetResize(iframeRef);
  if (!slug) return null;
  return (
    <Card className={`overflow-hidden shadow-lg ${dark ? "border-[#2c313d]" : "border-emerald-300"}`}>
      <div className="px-6 py-5 text-white" style={{ background: `linear-gradient(to right, ${primary || "#10b981"}, ${primary ? primary : "#0d9488"})` }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <CalendarCheck className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-xl">{data.heading || "Zarezerwuj online"}</h2>
            {data.subheading && <p className="text-sm text-white/90">{data.subheading}</p>}
          </div>
        </div>
      </div>
      <CardContent className="p-0">
        <iframe
          ref={iframeRef}
          title={`Rezerwacja — ${business.name}`}
          src={`https://slotify.dev.sorto.ai/widget/${slug}?lang=pl&theme=${dark ? "dark" : "light"}&primaryColor=${encodeURIComponent(primary || "#10b981")}`}
          style={{ width: "100%", minHeight: `${data.height || 850}px`, border: "none", display: "block" }}
          loading="lazy"
        />
      </CardContent>
    </Card>
  );
}

function TestimonialsBlock({ data, dark }) {
  const items = data.items || [];
  if (!items.length) return null;
  return (
    <Card className={dark ? "bg-[#1a1e26] border-[#2c313d] text-white" : ""}>
      <CardContent className="p-6">
        <h2 className="font-semibold text-lg mb-4">{data.heading || "Opinie klientow"}</h2>
        <div className="space-y-3">
          {items.map((t, i) => (
            <div key={i} className={`rounded-lg p-4 border ${dark ? "border-[#2c313d]" : "border-slate-200"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">{t.name || "(anonim)"}</span>
                <StarRating rating={t.rating} />
              </div>
              <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-600"}`}>{t.text || ""}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SocialBlock({ data, dark }) {
  const links = [
    { key: "facebook", label: "Facebook", url: data.facebook },
    { key: "instagram", label: "Instagram", url: data.instagram },
    { key: "tiktok", label: "TikTok", url: data.tiktok },
  ].filter((l) => l.url && isSafeHttpUrl(l.url));
  if (!links.length) return null;
  return (
    <Card className={dark ? "bg-[#1a1e26] border-[#2c313d] text-white" : ""}>
      <CardContent className="p-6">
        <h2 className="font-semibold text-lg mb-4">{data.heading || "Znajdz nas w sieci"}</h2>
        <div className="flex flex-wrap gap-3">
          {links.map(({ key, label, url }) => (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dark ? "bg-[#12151b] hover:bg-[#20242c] text-amber-400" : "bg-slate-100 hover:bg-slate-200 text-emerald-700"
              }`}
            >
              {label}
            </a>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ParkingWidgetBlock({ data, business, primary, dark }) {
  const iframeRef = useRef(null);
  useSlotifyWidgetResize(iframeRef);
  if (!data.widget_url || !isTrustedSlotifyWidgetUrl(data.widget_url)) return null;
  // Force the embedded Slotify widget's own theme to follow this page's mode,
  // regardless of what theme= was saved in widget_url — keeps them in sync
  // automatically instead of relying on someone editing the URL by hand.
  let widgetUrl = data.widget_url;
  try {
    const u = new URL(widgetUrl);
    u.searchParams.set('theme', dark ? 'dark' : 'light');
    u.searchParams.set('primaryColor', primary || '#10b981');
    widgetUrl = u.toString();
  } catch {
    // widget_url malformed — fall back to the raw stored value
  }
  return (
    <Card className={`overflow-hidden shadow-lg ${dark ? "border-[#2c313d]" : "border-emerald-300"}`}>
      <div className="px-6 py-5 text-white" style={{ background: `linear-gradient(to right, ${primary || "#10b981"}, ${primary ? primary : "#0d9488"})` }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <ParkingSquare className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-xl">{data.heading || "Zarezerwuj miejsce parkingowe"}</h2>
            {data.subheading && <p className="text-sm text-white/90">{data.subheading}</p>}
          </div>
        </div>
      </div>
      <CardContent className="p-0">
        <iframe
          ref={iframeRef}
          title={`Rezerwacja parkingu — ${business.name}`}
          src={widgetUrl}
          style={{ width: "100%", minHeight: `${data.height || 260}px`, border: "none", display: "block" }}
          loading="lazy"
        />
      </CardContent>
    </Card>
  );
}

const LANDING_PREMIUM_ICONS = {
  shield: Shield,
  car: Car,
  tag: Tag,
  plane: Plane,
  clock: Clock,
  check: CheckCircle2,
  phone: Phone,
  "map-pin": MapPin,
};

function LandingPremiumBlock({ data }) {
  const ref = useRef(null);
  const stats = data.stats || [];
  const steps = data.steps || [];
  const whyItems = data.why_us_items || [];

  const scrollToNext = () => {
    const next = ref.current?.closest(".landing-premium-wrap")?.nextElementSibling;
    if (next) next.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div ref={ref} className="landing-premium-wrap rounded-2xl overflow-hidden" style={{ background: "#12151b" }}>
      <div className="p-6 sm:p-10">
        {data.eyebrow && (
          <div
            className="inline-flex items-center gap-2 text-[11px] font-bold tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
            style={{ color: "#ffc267", background: "rgba(240,168,58,0.1)", border: "1px solid rgba(240,168,58,0.25)" }}
          >
            <span className="relative flex h-2 w-2">
              <span
                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                style={{ background: "#f0a83a" }}
              />
              <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#f0a83a" }} />
            </span>
            {data.eyebrow}
          </div>
        )}
        <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight text-white mb-4">
          {data.headline}
          {data.headline_emphasis && <span style={{ color: "#ffc267" }}> {data.headline_emphasis}</span>}
        </h1>
        {data.subheadline && (
          <p className="text-slate-300 text-base sm:text-lg max-w-xl mb-8">{data.subheadline}</p>
        )}
        {data.cta_text && (
          <button
            onClick={scrollToNext}
            className="inline-flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-lg mb-10"
            style={{ background: "#f0a83a", color: "#1a1305" }}
          >
            {data.cta_text}
          </button>
        )}
        {stats.length > 0 && (
          <div className="flex flex-wrap gap-x-10 gap-y-4">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-extrabold text-white tabular-nums">{s.value}</div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wide mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {steps.length > 0 && (
        <Reveal className="border-t px-6 sm:px-10 py-10" style={{ borderColor: "#2c313d" }}>
          <div
            className="text-[11px] font-bold tracking-widest uppercase mb-4"
            style={{ color: "#ffc267" }}
          >
            Jak to działa
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {steps.map((s, i) => (
              <div key={i} className="rounded-xl p-5 text-center sm:text-left" style={{ background: "#1a1e26", border: "1px solid #2c313d" }}>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-black text-sm mx-auto sm:mx-0"
                  style={{ background: "#0c0e12", color: "#ffc267" }}
                >
                  {i + 1}
                </div>
                <h3 className="text-white font-semibold mt-3 mb-1.5">{s.title}</h3>
                <p className="text-slate-400 text-sm">{s.description}</p>
              </div>
            ))}
          </div>
        </Reveal>
      )}

      {whyItems.length > 0 && (
        <Reveal
          className="border-t px-6 sm:px-10 py-10"
          style={{ borderColor: "#2c313d", background: "#1a1e26" }}
        >
          {data.why_us_heading && (
            <h2 className="text-xl font-bold text-white mb-6">{data.why_us_heading}</h2>
          )}
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
            {whyItems.map((item, i) => {
              const Icon = LANDING_PREMIUM_ICONS[item.icon] || Shield;
              return (
                <div key={i} className="flex gap-4">
                  <div
                    className="flex-none w-11 h-11 rounded-lg flex items-center justify-center"
                    style={{ background: "rgba(240,168,58,0.12)", color: "#ffc267" }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">{item.title}</h4>
                    <p className="text-slate-400 text-sm">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      )}
    </div>
  );
}

function FaqBlock({ data, dark }) {
  const items = data.items || [];
  if (!items.length) return null;
  return (
    <Reveal>
      <Card className={dark ? "bg-[#1a1e26] border-[#2c313d] text-white" : ""}>
        <CardContent className="p-6">
          <h2 className="font-semibold text-lg mb-4">{data.heading || "Najczęściej zadawane pytania"}</h2>
          <div className="space-y-3">
            {items.map((it, i) => (
              <details key={i} className={`group rounded-xl p-4 border ${dark ? "border-[#2c313d]" : "border-slate-200"}`}>
                <summary className="font-medium cursor-pointer list-none flex justify-between items-center gap-3">
                  <span>{it.question}</span>
                  <span className={`shrink-0 transition-transform group-open:rotate-180 ${dark ? "text-amber-400" : "text-emerald-600"}`}>▾</span>
                </summary>
                <p className={`text-sm mt-2 leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>{it.answer}</p>
              </details>
            ))}
          </div>
        </CardContent>
      </Card>
    </Reveal>
  );
}

const CONTACT_FORM_TIERS = ["business", "premium"];

function ContactForm({ business, primary, dark }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  if (!CONTACT_FORM_TIERS.includes(business.subscription_tier)) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact/${business.business_id}`, form);
      toast.success("Wiadomosc wyslana");
      setForm((f) => ({ ...f, message: "" }));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || "Nie udalo sie wyslac wiadomosci");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 pt-2">
      <Input
        required
        placeholder="Imie i nazwisko"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        className={dark ? "bg-[#12151b] border-[#2c313d] text-white" : ""}
      />
      <Input
        required
        type="email"
        placeholder="Email"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        className={dark ? "bg-[#12151b] border-[#2c313d] text-white" : ""}
      />
      <Input
        placeholder="Telefon (opcjonalnie)"
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
        className={dark ? "bg-[#12151b] border-[#2c313d] text-white" : ""}
      />
      <Textarea
        required
        placeholder="Wiadomosc"
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        className={dark ? "bg-[#12151b] border-[#2c313d] text-white" : ""}
      />
      <Button type="submit" disabled={sending} className="w-full" style={{ backgroundColor: primary || "#10b981" }}>
        {sending ? "Wysylanie..." : "Wyslij wiadomosc"}
      </Button>
    </form>
  );
}

function ContactBlock({ data, business, primary, dark }) {
  return (
    <Card className={dark ? "bg-[#1a1e26] border-[#2c313d] text-white" : ""}>
      <CardContent className="p-6 space-y-4">
        <h3 className="font-semibold text-lg">{data.heading}</h3>
        <div className="flex items-start gap-3">
          <MapPin className={`w-5 h-5 mt-0.5 ${dark ? "text-slate-500" : "text-slate-400"}`} />
          <div>
            <p className="text-sm font-medium">Adres</p>
            <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-600"}`}>{business.address}</p>
            <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-600"}`}>{business.city}</p>
          </div>
        </div>
        {business.phone && (
          <div className="flex items-start gap-3">
            <Phone className={`w-5 h-5 mt-0.5 ${dark ? "text-slate-500" : "text-slate-400"}`} />
            <div>
              <p className="text-sm font-medium">Telefon</p>
              <a href={`tel:${business.phone}`} className={`text-sm hover:underline ${dark ? "text-amber-400" : "text-emerald-700"}`}>{business.phone}</a>
            </div>
          </div>
        )}
        {business.website && isSafeHttpUrl(business.website) && (
          <div className="flex items-start gap-3">
            <Globe className={`w-5 h-5 mt-0.5 ${dark ? "text-slate-500" : "text-slate-400"}`} />
            <div>
              <p className="text-sm font-medium">Strona</p>
              <a href={business.website} target="_blank" rel="noopener noreferrer" className={`text-sm hover:underline break-all ${dark ? "text-amber-400" : "text-emerald-700"}`}>{business.website}</a>
            </div>
          </div>
        )}
        <ContactForm business={business} primary={primary} dark={dark} />
      </CardContent>
    </Card>
  );
}

function OpeningBlock({ data, business, dark }) {
  if (!business.opening_hours || !Object.keys(business.opening_hours).length) return null;
  return (
    <Card className={dark ? "bg-[#1a1e26] border-[#2c313d] text-white" : ""}>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className={`w-5 h-5 ${dark ? "text-slate-500" : "text-slate-400"}`} />
          <h3 className="font-semibold text-lg">{data.heading}</h3>
        </div>
        <div className="space-y-2">
          {Object.entries(business.opening_hours).map(([day, hours]) => (
            <div key={day} className="flex justify-between text-sm">
              <span className={dark ? "text-slate-400" : "text-slate-500"}>{day}</span>
              <span className="font-medium">{hours}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function MapBlock({ data, business, dark }) {
  if (!business.latitude || !business.longitude) return null;
  return (
    <Card className={dark ? "bg-[#1a1e26] border-[#2c313d] text-white" : ""}>
      <CardContent className="p-0">
        <div className="px-6 py-4">
          <h2 className="font-semibold text-lg">{data.heading}</h2>
        </div>
        <div className="h-64 overflow-hidden">
          <MapContainer center={[business.latitude, business.longitude]} zoom={15} className="h-full w-full" scrollWheelZoom={false}>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[business.latitude, business.longitude]}>
              <Popup>{business.name}</Popup>
            </Marker>
          </MapContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function CtaBlock({ data, primary }) {
  return (
    <Card className="border-0 shadow-lg overflow-hidden">
      <div className="p-8 text-center text-white" style={{ backgroundColor: primary || "#10b981" }}>
        <h2 className="text-2xl sm:text-3xl font-bold mb-2">{data.heading}</h2>
        {data.subheading && <p className="text-white/90 mb-6">{data.subheading}</p>}
        {data.button_text && data.button_link && isSafeHttpUrl(data.button_link) && (
          <a href={data.button_link} className="inline-block px-8 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors">
            {data.button_text}
          </a>
        )}
      </div>
    </Card>
  );
}

function sanitizeWhatsAppPhone(raw) {
  if (!raw) return "";
  // Strip "+", spaces, dashes, parentheses — wa.me expects digits only
  return String(raw).replace(/[\s+\-()]/g, "");
}

function WhatsAppBlock({ data, business, dark }) {
  const phone = sanitizeWhatsAppPhone(data.phone || business.phone);
  if (!phone) return null;
  const message = data.message || `Dzien dobry, mam pytanie dotyczace ${business.name}`;
  const link = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  const wa = "#25D366";
  return (
    <Card className={`overflow-hidden shadow-lg ${dark ? "border-[#2c313d]" : "border-0"}`}>
      <div className="px-6 py-5 text-white" style={{ backgroundColor: wa }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-xl">{data.heading || "Napisz do nas na WhatsApp"}</h2>
            {data.subheading && <p className="text-sm text-white/90">{data.subheading}</p>}
          </div>
        </div>
      </div>
      <CardContent className={`p-6 space-y-4 ${dark ? "bg-[#1a1e26] text-white" : ""}`}>
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: wa }}
        >
          <MessageCircle className="w-5 h-5" />
          Otworz WhatsApp
        </a>
        <p className={`text-xs ${dark ? "text-slate-500" : "text-slate-500"}`}>Szybka odpowiedz w godzinach pracy</p>
      </CardContent>
    </Card>
  );
}

function FloatingWhatsAppButton({ business }) {
  if (business?.whatsapp_enabled === false) return null;
  const phone = sanitizeWhatsAppPhone(business?.phone);
  if (!phone) return null;
  const message = `Dzien dobry, mam pytanie dotyczace ${business.name}`;
  const link = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      title="Napisz na WhatsApp"
      aria-label="Napisz na WhatsApp"
      className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105"
      style={{ backgroundColor: "#25D366" }}
    >
      <MessageCircle className="w-7 h-7 text-white" />
    </a>
  );
}

function renderBlock(block, business, primary, dark) {
  const map = {
    hero: HeroBlock,
    about: AboutBlock,
    services: (p) => <ListBlock {...p} />,
    why_us: (p) => <ListBlock {...p} />,
    gallery: GalleryBlock,
    slotify: SlotifyBlock,
    parking_widget: ParkingWidgetBlock,
    landing_premium: LandingPremiumBlock,
    faq: FaqBlock,
    whatsapp: WhatsAppBlock,
    testimonials: TestimonialsBlock,
    social: SocialBlock,
    opening: OpeningBlock,
    contact: ContactBlock,
    map: MapBlock,
    cta: CtaBlock,
  };
  const Component = map[block.type];
  if (!Component) return null;
  return <Component key={block.id} data={block.data} business={business} primary={primary} dark={dark} />;
}

// ============ MAIN COMPONENT ============

export default function SubdomainLandingPage({ subdomain, customDomain }) {
  const [business, setBusiness] = useState(null);
  const [builder, setBuilder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const lookupUrl = customDomain
          ? `${API}/businesses/by-domain/${customDomain}`
          : `${API}/businesses/by-subdomain/${subdomain}`;
        const { data: biz } = await axios.get(lookupUrl);
        setBusiness(biz);
        document.title = biz.name;
        try {
          const { data: bld } = await axios.get(`${API}/landing-pages/${biz.business_id}/builder`);
          setBuilder(bld);
        } catch (_) {
          setBuilder({ blocks: [], theme: {} });
        }
      } catch (e) {
        setError(e?.response?.status === 404 ? "Strona nie istnieje" : "Blad ladowania");
      }
    })();
  }, [subdomain, customDomain]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <h1 className="text-2xl font-bold mb-2">{error}</h1>
            <p className="text-slate-600 text-sm">Sprawdz, czy adres subdomeny jest poprawny.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!business || builder === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const primary = builder?.theme?.primary || "#10b981";
  const dark = builder?.theme?.mode === "dark";

  // Block-based rendering (when builder.blocks defined) or fallback default layout
  if (builder.blocks?.length) {
    const heroBlock = builder.blocks.find((b) => b.type === "hero");
    const nonHero = builder.blocks.filter((b) => b.type !== "hero");
    // Sidebar blocks (contact, opening, map) go to the right column; main content to the left
    const sidebarTypes = new Set(["contact", "opening", "map"]);
    const mainBlocks = nonHero.filter((b) => !sidebarTypes.has(b.type));
    const sidebarBlocks = nonHero.filter((b) => sidebarTypes.has(b.type));

    return (
      <div className={dark ? "min-h-screen" : "min-h-screen bg-slate-50"} style={dark ? { background: "#0c0e12" } : undefined}>
        {heroBlock && renderBlock(heroBlock, business, primary, dark)}

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10 pb-16">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {mainBlocks.map((b) => renderBlock(b, business, primary, dark))}
            </div>
            <div className="space-y-4">
              {sidebarBlocks.map((b) => renderBlock(b, business, primary, dark))}
            </div>
          </div>
        </div>

        <footer className={dark ? "border-t py-6 text-center text-xs" : "border-t border-slate-200 bg-white py-6 text-center text-xs text-slate-500"} style={dark ? { borderColor: "#2c313d", background: "#0c0e12", color: "#6b7280" } : undefined}>
          Strona oraz rezerwacja online zasilane przez{" "}
          <a href="https://lokalnie.pro" className={dark ? "hover:underline" : "text-emerald-700 hover:underline"} style={dark ? { color: "#ffc267" } : undefined}>lokalnie.pro</a>
        </footer>

        <FloatingWhatsAppButton business={business} />
      </div>
    );
  }

  // Fallback (no builder content yet): minimal default layout
  return (
    <div className="min-h-screen bg-slate-50">
      <HeroBlock data={{ use_business_photo: true }} business={business} primary={primary} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10 pb-16">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {business.description && <AboutBlock data={{ heading: "O firmie", body: business.description }} />}
            {business.slotify_slug && <SlotifyBlock data={{ heading: "Zarezerwuj online", subheading: "Wybierz uslugodawce, date i godzine.", slug: business.slotify_slug, height: 850 }} business={business} primary={primary} />}
            <MapBlock data={{ heading: "Lokalizacja" }} business={business} />
          </div>
          <div className="space-y-4">
            <ContactBlock data={{ heading: "Kontakt" }} business={business} />
            <OpeningBlock data={{ heading: "Godziny otwarcia" }} business={business} />
          </div>
        </div>
      </div>
      <footer className="border-t border-slate-200 bg-white py-6 text-center text-xs text-slate-500">
        Strona oraz rezerwacja online zasilane przez{" "}
        <a href="https://lokalnie.pro" className="text-emerald-700 hover:underline">lokalnie.pro</a>
      </footer>

      <FloatingWhatsAppButton business={business} />
    </div>
  );
}
